/**
 * Duration formatting for millisecond inputs.
 *
 * Four named styles cover every existing portal use case:
 *
 *   - `'compact'`   (default) — promotes to hours, omits zero parts.
 *     Sub-second → "500ms"; <10s → "1.5s"; ≥10s → "15s";
 *     ≥1m → "1m 5s" / "2m" (drops 0-seconds);
 *     ≥1h → "1h 2m 5s" / "1h" (drops zero parts).
 *     Used by analytics charts and general "how long" copy.
 *
 *   - `'analytics'` — caps at minutes, fixed sub-minute precision,
 *     drops zero seconds at the minute mark. Sub-second → integer ms;
 *     <1m → "1.0s" / "59.9s"; ≥1m → "1m" / "1m 30s".
 *     Matches the legacy `utils/formatDuration.ts` used by KPI cards so
 *     adjacent rows have aligned decimal columns.
 *
 *   - `'execution'` — caps at minutes with a stable seconds slot for
 *     column-aligned monitoring reads. <1s → "42ms";
 *     <1m → "1.0s"; ≥1m → "1m 0s" / "2m 30s".
 *     Matches `formatExecutionDuration` so MonitoringView rows align.
 *
 *   - `'overlay'`   — single-segment monitoring overlay style.
 *     Sub-second → "42ms"; <1m → "1.5s"; ≥1m → "1.5m".
 *     Used by overlays and tight badges that never split minute+second.
 *
 * All styles defend against rounding overflow ("1m 60s" never appears).
 *
 * The default `fallback` is an em-dash (`'—'`). Pass any other sentinel
 * (e.g. `'--'`, `''`, `'-'`) at the call site to preserve a specific
 * existing UX.
 */

import { MS_PER_SECOND, MS_PER_MINUTE, MS_PER_HOUR } from './constants';

export type FormatDurationStyle = 'compact' | 'analytics' | 'execution' | 'overlay';

export interface FormatDurationOptions {
  /** Display preset. Default `'compact'`. */
  style?: FormatDurationStyle;
  /** Sentinel for null/undefined/NaN input. Default `'—'`. */
  fallback?: string;
}

export function formatDuration(
  ms: number | null | undefined,
  options: FormatDurationOptions = {},
): string {
  const { style = 'compact', fallback = '—' } = options;

  if (ms === undefined || ms === null || Number.isNaN(ms)) {
    return fallback;
  }

  switch (style) {
    case 'analytics':
      return formatAnalytics(ms);
    case 'execution':
      return formatExecution(ms);
    case 'overlay':
      return formatOverlay(ms);
    default:
      return formatCompact(ms);
  }
}

function formatCompact(ms: number): string {
  if (ms < MS_PER_SECOND) return `${ms}ms`;

  if (ms < MS_PER_MINUTE) {
    const seconds = ms / MS_PER_SECOND;
    return seconds < 10 ? `${seconds.toFixed(1)}s` : `${Math.floor(seconds)}s`;
  }

  if (ms < MS_PER_HOUR) {
    const minutes = Math.floor(ms / MS_PER_MINUTE);
    const seconds = Math.floor((ms % MS_PER_MINUTE) / MS_PER_SECOND);
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(ms / MS_PER_HOUR);
  const minutes = Math.floor((ms % MS_PER_HOUR) / MS_PER_MINUTE);
  const seconds = Math.floor((ms % MS_PER_MINUTE) / MS_PER_SECOND);
  const parts = [`${hours}h`];
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);
  return parts.join(' ');
}

function formatAnalytics(ms: number): string {
  if (ms < MS_PER_SECOND) return `${Math.round(ms)}ms`;
  if (ms < MS_PER_MINUTE) return `${(ms / MS_PER_SECOND).toFixed(1)}s`;

  let minutes = Math.floor(ms / MS_PER_MINUTE);
  let seconds = Math.round((ms % MS_PER_MINUTE) / MS_PER_SECOND);
  if (seconds >= 60) {
    minutes++;
    seconds = 0;
  }
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

function formatExecution(ms: number): string {
  if (ms < MS_PER_SECOND) return `${ms}ms`;
  if (ms < MS_PER_MINUTE) return `${(ms / MS_PER_SECOND).toFixed(1)}s`;

  const totalSeconds = Math.round(ms / MS_PER_SECOND);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

function formatOverlay(ms: number): string {
  if (ms < MS_PER_SECOND) return `${ms}ms`;
  if (ms < MS_PER_MINUTE) return `${(ms / MS_PER_SECOND).toFixed(1)}s`;
  return `${(ms / MS_PER_MINUTE).toFixed(1)}m`;
}
